import { getNuclearPlantsAvailability } from "$/api/market-monitoring/fundamentals.api";
import { Loader } from "$/lib/components/atoms/Loader/Loader";
import Flexbox from "$/lib/components/common/Flexbox";
import ComposedLineGraph from "$/lib/components/graphs/ComposedLineGraph";
import GraphFilter from "$/lib/components/graphs/GraphFilter/GraphFilter";
import type { GraphFilterItemsProps } from "$/lib/components/graphs/GraphFilter/GraphFilterItem";
import ChartsContainer from "$/lib/components/molecules/ChartsContainer";
import DisplayBox from "$/lib/components/molecules/DisplayBox";
import FiltersDisplayBox from "$/lib/components/molecules/FiltersDisplayBox";
import LineChartIcon from "$/lib/icons/LineChartIcon";
import type { CustomDashboardProps } from "$/lib/types/MarketMonitoring.types";
import { DEFAULT_GRAPH_COLORS } from "$/lib/utils/functions/marketMonitoring/graphs.functions";
import { cn, getArrayKeys } from "$/lib/utils/functions/misc.functions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const COLORS = [...DEFAULT_GRAPH_COLORS];

const NuclearPlantsAvailability = ({
  id,
  isDraggable = false,
  isShowcase = false,
}: CustomDashboardProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ["get-national-nuclear-plants-availability"],
    queryFn: () => getNuclearPlantsAvailability(),
  });

  const keys = useMemo(
    () => getArrayKeys(data ?? [], "date", undefined, true),
    [data],
  );

  const areaChartKey = keys.find((key) => key.includes("Enveloppe"));

  const colors: Record<string, string> = keys.reduce(
    (acc, key, i) => ({
      ...acc,
      [key]: COLORS[i],
    }),
    {},
  );

  const [graphVisibility, setGraphVisibility] = useState<
    GraphFilterItemsProps[]
  >([]);

  const handleOnChangeGraphVisiblity = (key: string, value: boolean) => {
    setGraphVisibility((prev) =>
      prev.map((item) =>
        item.name === key ? { ...item, isVisible: value } : item,
      ),
    );
  };

  useEffect(() => {
    setGraphVisibility([
      { name: areaChartKey ?? "", color: "#a4a5b2" },
      ...keys
        .filter((key) => key !== areaChartKey)
        .map((key, i) => ({
          name: key,
          isVisible: true,
          color: COLORS[i],
        })),
    ]);
  }, [areaChartKey, keys]);

  return (
    <DisplayBox
      title="Disponibilité du parc nucléaire Français"
      containerClassName={cn("shadow-displayBoxShadow", {
        "pointer-events-none": isShowcase,
        "w-full": isDraggable || isShowcase,
      })}
      Icon={LineChartIcon}
      theme="orange"
      id={id}
      isDraggable={isDraggable}
    >
      <ChartsContainer className="!h-[510px] py-0">
        {isLoading ? (
          <Flexbox fullWidth fullHeight align="center" justify="center">
            <Loader height={40} width={40} />
          </Flexbox>
        ) : (
          <ComposedLineGraph
            data={data ?? []}
            xAxisDataKey="date"
            areaChartDataKey={areaChartKey ?? ""}
            areaChartColor="#a4a5b2"
            keys={keys}
            colors={colors}
            unit=" GW"
            graphVisibility={graphVisibility}
            theme="orange"
            leftMargin={-40}
            formatStr="dd MMM"
          />
        )}

        <FiltersDisplayBox>
          <GraphFilter
            title="Légende"
            data={graphVisibility}
            handleOnChange={handleOnChangeGraphVisiblity}
          />
        </FiltersDisplayBox>
      </ChartsContainer>
    </DisplayBox>
  );
};

export default NuclearPlantsAvailability;
